import type { LinkedAccommodationItemComponent } from '@/cms/fields/types/common'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useId, useState } from 'react'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './AccommodationLinks.module.css'

const SHOW_ALL_TRIGGER_POSITION = 15

interface AccommodationLinksProps {
  accommodations: LinkedAccommodationItemComponent[]
}

function AccommodationLink({ accommodation }: { accommodation: LinkedAccommodationItemComponent }) {
  return (
    <a className={styles.accommodationLink} href={accommodation.url} target="_blank">
      <ListItem className={styles.accommodationTile} variant="tinted" hasHover>
        <Typography>{accommodation.name}</Typography>
        <Icon glyph="ChevronRight" size={20} />
      </ListItem>
    </a>
  )
}

export default function AccommodationLinks({ accommodations }: AccommodationLinksProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const [isExpanded, setIsExpanded] = useState(false)
  const hiddenAccommodationsId = useId()
  const shouldCollapse = accommodations.length > SHOW_ALL_TRIGGER_POSITION

  const visibleAccommodations = shouldCollapse
    ? accommodations.slice(0, SHOW_ALL_TRIGGER_POSITION - 1)
    : accommodations
  const hiddenAccommodations = shouldCollapse
    ? accommodations.slice(SHOW_ALL_TRIGGER_POSITION - 1)
    : []

  const handleShowAll = () => {
    setIsExpanded(true)
    sendEvent({
      event: 'CRO',
      description: 'Shows all linked accommodations',
      element: 'NL',
      type: 'button',
      result: 'show more accommodations',
      interaction: true,
    })
  }

  return (
    <div className={styles.accommodationLinks}>
      {visibleAccommodations.map(accommodation => (
        <AccommodationLink key={accommodation.id} accommodation={accommodation} />
      ))}
      {shouldCollapse && !isExpanded && (
        <button
          aria-controls={hiddenAccommodationsId}
          aria-expanded={false}
          className={styles.showAllButton}
          onClick={handleShowAll}
          type="button"
        >
          <ListItem className={styles.accommodationTile} variant="tinted" hasHover>
            <Typography>{intl.formatMessage({ id: 'linked_accommodations_show_all' })}</Typography>
            <Icon glyph="ChevronDown" size={20} />
          </ListItem>
        </button>
      )}
      {shouldCollapse && (
        <div id={hiddenAccommodationsId} className={styles.hiddenAccommodations} hidden={!isExpanded}>
          {hiddenAccommodations.map(accommodation => (
            <AccommodationLink key={accommodation.id} accommodation={accommodation} />
          ))}
        </div>
      )}
    </div>
  )
}
